/**
 * Visitor badge — print-friendly 2"x2" (50mm x 50mm) card.
 *
 * Designed for **black & white thermal/laser printers** — no colour fills,
 * no gradients, no faint grays. All structural lines are black; all text is
 * solid black on white; the QR code is naturally B&W.
 *
 * Sizing:
 *   - Default: 50mm x 50mm (≈ 2" x 2"). Set `printSizeMm={n}` to override.
 *   - On-screen: scales to its container with the same aspect ratio.
 *   - When `window.print()` runs, page-level CSS in `index.css` hides
 *     everything except `.print-badge`, so the badge prints centred at
 *     exactly the requested size.
 *
 * The badge intentionally omits the photo and the NDA-accepted pill — at
 * 2"x2" they're not legible and they bloat the toner budget. Both fields
 * are still visible in the in-app badge / details page.
 */
import React from "react";

function formatShortDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function VisitorBadge({ visitor, printSizeMm = 50 }) {
    if (!visitor) return null;
    const qr = visitor.badge_qr || visitor.checkin_qr;
    const host = [visitor.host_name, visitor.host_room_name].filter(Boolean).join(" · ");

    // The badge is a fixed-size square in millimetres so it prints exactly
    // 50mm x 50mm regardless of screen DPI.
    const size = `${printSizeMm}mm`;

    return (
        <div
            className="print-badge bg-white text-black"
            style={{
                width: size,
                height: size,
                border: "1.5pt solid #000",
                boxSizing: "border-box",
                padding: "2mm",
                display: "flex",
                flexDirection: "column",
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: "7pt",
                lineHeight: 1.15,
                color: "#000",
            }}
            data-testid="visitor-badge"
        >
            {/* Header: brand + VISITOR tag, separated by a black rule */}
            <div
                style={{
                    display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    paddingBottom: "1mm", borderBottom: "0.75pt solid #000",
                }}
            >
                <div
                    style={{ fontWeight: 800, fontSize: "9pt", letterSpacing: "0.3pt" }}
                    data-testid="visitor-badge-brand"
                >
                    TekWissen
                </div>
                <div style={{ fontWeight: 700, fontSize: "7pt", letterSpacing: "0.6pt" }}>
                    VISITOR
                </div>
            </div>

            {/* Body: QR + identity */}
            <div style={{ display: "flex", gap: "2mm", flex: 1, marginTop: "1.5mm", minHeight: 0 }}>
                {qr && (
                    <img
                        src={qr}
                        alt="badge QR"
                        style={{
                            width: "24mm", height: "24mm",
                            // No padding/border around the QR — every black pixel matters
                            // for scan reliability on cheap B&W printers.
                            objectFit: "contain", flexShrink: 0,
                        }}
                        data-testid="visitor-badge-qr"
                    />
                )}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.8mm" }}>
                    <div
                        style={{
                            fontWeight: 800, fontSize: "9pt", lineHeight: 1.05,
                            overflow: "hidden", textOverflow: "ellipsis",
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                        }}
                        data-testid="visitor-badge-name"
                    >
                        {visitor.name}
                    </div>
                    {visitor.company && (
                        <div
                            style={{
                                fontSize: "6.5pt", fontWeight: 500,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}
                        >
                            {visitor.company}
                        </div>
                    )}
                    <div style={{ marginTop: "0.5mm" }}>
                        <div style={{ fontSize: "5.5pt", fontWeight: 700, letterSpacing: "0.3pt" }}>HOST</div>
                        <div
                            style={{
                                fontSize: "6.5pt", fontWeight: 600, lineHeight: 1.1,
                                overflow: "hidden", textOverflow: "ellipsis",
                                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            }}
                            data-testid="visitor-badge-host"
                        >
                            {host || "—"}
                        </div>
                    </div>
                    <div style={{ marginTop: "auto" }}>
                        <div style={{ fontSize: "5.5pt", fontWeight: 700, letterSpacing: "0.3pt" }}>PIN</div>
                        <div
                            style={{ fontFamily: "Menlo, Consolas, monospace", fontWeight: 800, fontSize: "9pt", letterSpacing: "1pt" }}
                            data-testid="visitor-badge-pin"
                        >
                            {visitor.pin || "—"}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer: visit time + valid until on a single line */}
            <div
                style={{
                    borderTop: "0.75pt solid #000",
                    paddingTop: "1mm", marginTop: "1mm",
                    display: "flex", justifyContent: "space-between", gap: "1mm",
                    fontSize: "5.5pt",
                }}
            >
                <div style={{ minWidth: 0, overflow: "hidden" }}>
                    <span style={{ fontWeight: 700, letterSpacing: "0.3pt" }}>VISIT</span>{" "}
                    <span data-testid="visitor-badge-visit-time">{formatShortDateTime(visitor.expected_at)}</span>
                </div>
                <div style={{ minWidth: 0, overflow: "hidden", textAlign: "right" }}>
                    <span style={{ fontWeight: 700, letterSpacing: "0.3pt" }}>VALID</span>{" "}
                    <span>{formatShortDateTime(visitor.valid_until)}</span>
                </div>
            </div>
        </div>
    );
}
